import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const TogoPage = () => {
  const togoData = {
    name: 'Togo',
    region: 'West Africa',
    color: '#606090',
    metaTitle: 'Pharma & Nutraceutical Registration in Togo | CTD / WA-MRH | TheApostrophe',
    metaDescription: 'Discover how to register pharmaceutical and nutraceutical products in Togo. Dossier requirements, CTD/WA-MRH alignment, key documents, regulatory framework, and market opportunity.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Togo',
      subtitle: 'A West African import-dependent market aligned with WA-MRH harmonisation, offering growing opportunities for compliant Indian manufacturers and nutraceutical exporters.',
      cta: 'Start Your Togo Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~8.7M' },
      { label: 'Income Level', value: 'Low-income economy' },
      { label: 'Pharmaceutical Market Size', value: '~USD 278.6M (2023)' },
      { label: 'Health Expenditure (% of GDP)', value: '~3-4%' },
      { label: 'Urbanisation', value: '~45-50% urban' },
      { label: 'Language', value: 'French official' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Import-Reliant & Harmonised',
      stats: [
        'Togo is a member of WAHO and participates in WA-MRH programme',
        'Pharmaceuticals market reached approx. USD 278.59 million in 2023',
        'National regulatory body: Direction de la Pharmacie et du Médicament (DPM) Togo',
        'Nutraceutical and supplement segments emerging in urban retail and e-pharmacy channels',
        'Predictable CTD-aligned regulatory route with French-language preference',
        'Strategic gateway market for Francophone West Africa'
      ],
      opportunities: [
        'Manageable documentation requirements via WA-MRH',
        'Targeted investment opportunity',
        'Access via Lome port for regional distribution',
        'Growing e-commerce and online pharmacy channels'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Landscape: Togo\'s Healthcare Needs',
      subtitle: 'High infectious disease burden with emerging NCDs and growing nutraceutical opportunities.',
      categories: [
        {
          name: 'Infectious Diseases',
          items: [
            'High burdens of malaria, TB and diarrhoeal diseases',
            'Informal medicine markets indicate both demand and regulatory gaps',
            'Self-medication prevalent in many areas',
            'Portfolio: Anti-infectives, antimalarials, antibiotics, ORS + zinc',
            'Paediatric care products in high demand'
          ]
        },
        {
          name: 'Non-Communicable Diseases',
          items: [
            'Urbanisation bringing hypertension, diabetes and cardiovascular risk',
            'Rising middle-class health awareness',
            'Portfolio: Antihypertensives, antidiabetics, cardiovascular care',
            'Metabolic health products gaining traction',
            'Chronic disease management growing segment'
          ]
        },
        {
          name: 'Nutraceutical Opportunities',
          items: [
            'Immunity support products',
            'Micronutrient supplements',
            'Women\'s & children\'s health products',
            'Prenatal & paediatric supplementation',
            'General wellness formulas',
            'E-commerce channels expanding reach'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Direction de la Pharmacie et du Médicament (DPM) Togo',
      description: 'Togo participates in WA-MRH (West African Medicines Regulatory Harmonization) and uses ECOWAS harmonised CTD format for pharmaceuticals. French language mandatory for administrative and labelling documents. Local MAH or authorised importer required.',
      pharma: {
        timeline: 'Varies based on dossier completeness and DPM review process',
        requirements: [
          'CTD dossier format through WA-MRH / ECOWAS harmonisation',
          'Module 1 - Local admin documents in French',
          'Modules 2-5 - Quality/safety/efficacy documentation',
          'GMP certificate (Indian GMP acceptable)',
          'Stability data for Zone IVb climate',
          'Submission, review and query resolution process',
          'Marketing Authorisation with renewal requirements'
        ],
        insight: 'Togo\'s use of WA-MRH format helps align with other Francophone West African markets, making it ideal for portfolio expansion across the region.'
      },
      nutra: {
        timeline: 'Depends on classification and documentation completeness',
        requirements: [
          'Regulated under food / dietary supplement frameworks',
          'French labelling required',
          'Claims must not treat disease',
          'GMP, COA and classification reports necessary',
          'Safety dossier for novel ingredients',
          'French-language artwork compliance',
          'Certificate Free Sale (CFS) for imports'
        ],
        insight: 'Emerging opportunity through e-commerce and online pharmacy channels. Growing urban demand for supplements aligned with wellness trends.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharma Registration (CTD – WA-MRH)',
      steps: [
        'Appoint local MAH/importer in Togo',
        'Prepare CTD dossier - Module 1 (local admin, French) and Modules 2-5',
        'GMP certificate submission (Indian GMP acceptable)',
        'Stability data for Zone IVb climate',
        'Dossier submission to DPM Togo',
        'Review process and query resolution',
        'Marketing Authorisation granted',
        'Renewal requirements as per DPM guidelines'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical & Dietary Supplement Registration',
      steps: [
        'Classification under food / dietary supplement frameworks',
        'Complete formula preparation',
        'COA & microbial limits documentation',
        'GMP/quality certification',
        'Safety dossier for novel ingredients',
        'French-language artwork preparation',
        'Claims justification (no disease treatment claims)',
        'Certificate Free Sale (CFS) for imports',
        'DPM review and approval',
        'Marketing authorization issuance'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (CTD)': [
        'Application form + Module 1 (French)',
        'CTD Modules 2-5',
        'GMP certificate',
        'CoPP (if available)',
        'Stability data (Zone IVb)',
        'API/DMF info',
        'Labels/inserts in French',
        'COA & finished product specs'
      ],
      'Nutraceuticals': [
        'Complete formula',
        'COA & microbial limits',
        'GMP/quality certification',
        'Safety dossier if novel ingredients',
        'French-language artwork',
        'Claims justification',
        'Certificate Free Sale (CFS) for imports'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Togo Should Be On Your West Africa Strategy',
      reasons: [
        'Predictable regulatory alignment through WA-MRH',
        'Import-dependent market ~USD 278 million size (2023)',
        'French-language market - strategic for Francophone West Africa',
        'Growing urban demand + niche nutraceutical channel growth',
        'Easier logistics via Lome port',
        'Strategic entry point for ECOWAS/Francophone Africa',
        'Manageable market size for targeted investment',
        'E-commerce and online pharmacy channels expanding'
      ],
      bestPortfolio: [
        'Generics: antimalarials, antihypertensives, paediatrics',
        'Nutra: prenatal & paediatric supplements, immunity formulas, metabolic health'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Togo',
      services: [
        'Market & portfolio assessment for Togo',
        'CTD/WA-MRH dossier preparation in French/admin + technical modules',
        'Nutra classification & documentation',
        'French-language label & artwork adaptation',
        'Importer coordination (setup MAH)',
        'Query handling, renewal and lifecycle support'
      ]
    },
    
    faqs: [
      {
        question: 'Does Togo require CTD format?',
        answer: 'Yes. Through WA-MRH, Togo uses CTD for pharmaceuticals as part of ECOWAS harmonisation.'
      },
      {
        question: 'Is French mandatory on labels?',
        answer: 'Yes. French is the official language and required for administrative and packaging documents.'
      },
      {
        question: 'Are Indian GMP certificates accepted?',
        answer: 'Yes. Indian GMP is broadly accepted in Francophone West Africa when documented properly.'
      },
      {
        question: 'Do supplements need registration in Togo?',
        answer: 'Yes, imported supplements must meet local registration/notification requirements.'
      },
      {
        question: 'Can Togo be used as a gateway to other Francophone markets?',
        answer: 'Yes. Its alignment with WA-MRH and access via Lome port make it a strategic entry point for ECOWAS/Francophone Africa.'
      }
    ]
  };

  return <CountryTemplate countryData={togoData} />;
};
